import { BooleanInput } from '@angular/cdk/coercion';
import { AsyncPipe, NgFor, NgIf, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Router, RouterLink } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { HasRoleDirective } from 'app/modules/shared/has-role.directive';
import { getInitials } from 'app/modules/shared/utils';
import { PaywallService } from 'app/modules/shared/paywall/paywall.service';
import { Subscription, SubscriptionEntitlement } from 'app/modules/shared/paywall/paywall.models';
import { Observable, Subject, catchError, map, of, shareReplay, takeUntil } from 'rxjs';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        MatButtonModule, MatMenuModule, NgIf, NgFor, AsyncPipe,
        MatIconModule, MatDividerModule, MatProgressBarModule,
        RouterLink, UpperCasePipe, HasRoleDirective,
    ]
})
export class UserComponent implements OnInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    storeInitials: string;
    @Input() showAvatar: boolean = true;
    user: User;

    subscription$: Observable<Subscription | null>;
    quotaEntitlements$: Observable<SubscriptionEntitlement[] | null>;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _paywall: PaywallService,
    ) {}

    ngOnInit(): void
    {
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) =>
            {
                this.user = user;
                this.storeInitials = getInitials(user.name);
                this._changeDetectorRef.markForCheck();
            });

        this.subscription$ = this._paywall.getSubscription().pipe(
            catchError(() => of(null)),
            shareReplay(1),
        );

        this.quotaEntitlements$ = this.subscription$.pipe(
            map(sub => sub?.entitlements?.filter(
                e => e.entitlement_type === 'quota_based'
                    && (e.quota_limit ?? 0) > 0
                    && e.quota_period !== 'lifetime'
            ) ?? null),
        );
    }

    ngOnDestroy(): void
    {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    

    signOut(): void
    {
        this._router.navigate(['/sign-out']);
    }

    quotaPercent(entitlement: SubscriptionEntitlement): number
    {
        if (!entitlement.quota_limit || entitlement.quota_used == null)
        {
            return 0;
        }

        return Math.min(100, (entitlement.quota_used / entitlement.quota_limit) * 100);
    }
}
